import { Injectable, inject } from "@angular/core";
import { API_BASE_URL } from "../../core/base.service";
import { AuthService } from "../../core/auth.service";
import { createNativeFile } from "../utils/native-file";

/**
 * 跨平台文件下载服务
 *
 * Cordova App 环境：
 *   直接构造带 token 的 API 下载链接 → InAppBrowser _system 打开
 *   → 系统浏览器下载到公共「下载」目录
 *
 * 纯浏览器环境：blob URL + a.click()
 */
@Injectable({ providedIn: "root" })
export class DownloadService {
  private readonly apiBase = inject(API_BASE_URL, { optional: true }) || "";
  private readonly auth = inject(AuthService);
  readonly isApp = !!(window as any)["cordova"];

  /**
   * 下载 Blob 为文件（浏览器环境）
   */
  download(blob: Blob, fileName: string): void {
    if (this.isApp) {
      // App 环境：先尝 Web Share，失败则走系统浏览器下载
      this.downloadApp(blob, fileName);
    } else {
      this.downloadBrowser(blob, fileName);
    }
  }

  /**
   * 直接通过系统浏览器打开下载链接（推荐 Cordova 环境使用）
   *
   * @param apiPath  API 路径，如 "/student/ExportPdf" 或 "/course/export?..."
   * @param params   查询参数（不含 token，自动追加）
   */
  private get webBase(): string { return this.apiBase.replace(/\/api$/, ""); }

  /** 打开 API 下载链接（自动带 token） */
  openDownloadUrl(apiPath: string, params?: Record<string, string | number | undefined>): void {
    const token = this.auth.getToken();
    const uid = this.auth.getUID();
    let url = `${this.apiBase}${apiPath}`;
    if (params) {
      for (const [k, v] of Object.entries(params)) {
        if (v != null) url += `${url.includes("?") ? "&" : "?"}${k}=${encodeURIComponent(String(v))}`;
      }
    }
    url += `${url.includes("?") ? "&" : "?"}token=${token}&uid=${uid}`;
    this.openUrl(url);
  }

  /** 打开网页静态文件（不带 auth 参数） */
  openWebUrl(webPath: string): void {
    this.openUrl(`${this.webBase}${webPath}`);
  }

  /**
   * 下载网页静态文件（如 /Daemon/... 的附件/图片，不带 auth）。
   * - App：交给系统浏览器（InAppBrowser _system）下载到系统下载目录；
   * - 纯浏览器：fetch 为 Blob 后触发本地保存；跨域/CORS 失败时回退到新标签打开。
   * @param url   文件完整 URL
   * @param fileName 下载文件名（可选；缺省用 URL 最后一段）
   */
  downloadWebFile(url: string, fileName?: string): void {
    if (!url) return;
    if (this.isApp) {
      // 系统浏览器自动下载到「下载」目录，无需 fetch（可避开 WebView 的 CORS/沙箱限制）
      this.openUrl(url);
      return;
    }
    this.saveBrowserFile(url, fileName || this.defaultFileName(url));
  }

  /**
   * 下载库里存的静态文件路径。
   * DB 中保存的是相对路径 serverFilePath（如 /Daemon/20260907/docx/xxx.docx），
   * 这里按当前 webBase 拼成完整 URL 再下载；兼容历史已存完整 URL 的旧数据（http 开头直接用）。
   * @param storedPath  相对路径（/ 开头）或完整 URL
   * @param fileName    下载文件名（可选）
   */
  downloadStoredFile(storedPath: string, fileName?: string): void {
    if (!storedPath) return;
    const full =
      /^https?:\/\//i.test(storedPath)
        ? storedPath
        : `${this.webBase}${storedPath.startsWith("/") ? storedPath : `/${storedPath}`}`;
    this.downloadWebFile(full, fileName || this.defaultFileName(storedPath));
  }

  // ==================== 内部 ====================

  private downloadBrowser(blob: Blob, fileName: string): void {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = fileName;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); window.URL.revokeObjectURL(url);
  }

  private downloadApp(blob: Blob, fileName: string): void {
    const nav = navigator as any;
    if (nav.share) {
      // 打包 App 内 window.File 被 cordova-plugin-file 替换，必须走原生构造，
      // 否则分享面板里的文件名会是畸形对象
      const file = createNativeFile([blob], fileName, {
        type: blob.type || "application/octet-stream",
      });
      nav.share({ files: [file], title: fileName }).catch(() => this.openBlobInBrowser(blob));
      return;
    }
    this.openBlobInBrowser(blob);
  }

  /** blob → data URL → App 内 InAppBrowser 打开大图（兜底方案）
   * 注意：不能走 _system，Android Chrome/WebView 会拦截顶层 data: URL 导航；
   * 此方法仅用于 App 内展示图片（家长可截图保存），不影响 openDownloadUrl 的系统下载。 */
  private openBlobInBrowser(blob: Blob): void {
    const downloadBlob = new Blob([blob], { type: "application/octet-stream" });
    const r = new FileReader();
    r.onloadend = () => {
      try {
        const c = (window as any)["cordova"];
        if (c?.InAppBrowser) { c.InAppBrowser.open(r.result as string, "_blank"); }
        else { window.open(r.result as string, "_blank"); }
      } catch { /* 静默 */ }
    };
    r.onerror = () => {};
    r.readAsDataURL(downloadBlob);
  }

  private openUrl(url: string): void {
    try {
      const c = (window as any)["cordova"];
      if (c?.InAppBrowser) { c.InAppBrowser.open(url, "_system"); }
      else { window.open(url, "_system"); }
    } catch { /* 静默 */ }
  }

  /** 纯浏览器：fetch 静态文件为 Blob 后触发保存；失败回退新标签打开 */
  private async saveBrowserFile(url: string, fileName: string): Promise<void> {
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      this.downloadBrowser(blob, fileName);
    } catch {
      try { window.open(url, "_blank"); } catch { /* 静默 */ }
    }
  }

  /** URL 最后一段作为缺省文件名 */
  private defaultFileName(url: string): string {
    const clean = (url || "").split(/[?#]/)[0];
    const idx = clean.lastIndexOf("/");
    const seg = idx >= 0 ? clean.substring(idx + 1) : clean;
    try { return decodeURIComponent(seg) || "file"; } catch { return seg || "file"; }
  }
}
